import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ActionIcon, Text, Badge, Menu } from '@mantine/core';
import { IconTrash, IconDeviceFloppy, IconTemplate, IconCheck, IconX, IconPlus } from '@tabler/icons-react';
import { SavedReport } from '@turizi/shared';
import CardModal from '@/components/card-modal';

interface ReservationExportTemplateControllerProps {
    savedReports: SavedReport[];
    selectedPresetId: string | null;
    onSelectPreset: (preset: SavedReport | null) => void;
    onDeletePreset: (id: string) => void;
    onSaveClick: () => void;
    onUpdatePreset: () => void;
}

export default function ReservationExportTemplateController({
    savedReports,
    selectedPresetId,
    onSelectPreset,
    onDeletePreset,
    onSaveClick,
    onUpdatePreset,
}: ReservationExportTemplateControllerProps) {
    const { t } = useTranslation();
    const [opened, setOpened] = useState(false);

    const selectedPreset = savedReports.find(r => r.id.toString() === selectedPresetId);

    return (
        <div className="space-y-4 bg-white border border-gray-200 rounded-lg p-4 h-full flex flex-col justify-between">
            {/* Active Template Display Header */}
            <div className="border-b border-slate-100 pb-6">
                <Text size="xs" fw={700} className="text-slate-500 uppercase tracking-wider">
                    {t('presets_templates')}
                </Text>
            </div>

            {/* Selection & Action Row */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <div
                    onClick={() => setOpened(true)}
                    className="flex-1 flex items-center justify-between p-2 rounded-lg border border-gray-200 bg-slate-50/30 hover:bg-slate-55 hover:border-gray-300 cursor-pointer transition-all min-h-[36px]"
                >
                    <div className="flex items-center gap-2 text-sm">
                        <IconTemplate size={16} className="text-slate-400" />
                        <div className={selectedPreset ? 'text-slate-805 font-bold' : 'text-slate-400'}>
                            {selectedPreset ? selectedPreset.name : t('select_template')}
                        </div>
                    </div>
                </div>

                <div className="flex gap-2 justify-end">
                    {selectedPresetId && (
                        <Button
                            variant="light"
                            color="gray"
                            onClick={() => onSelectPreset(null)}
                            size="sm"
                            className="h-[36px] font-medium"
                        >
                            <IconX size={16} />
                        </Button>
                    )}

                    {selectedPresetId ? (
                        <Menu shadow="md" width={220} position="bottom-end" withinPortal>
                            <Menu.Target>
                                <Button
                                    variant="light"
                                    color="indigo"
                                    leftSection={<IconDeviceFloppy size={16} />}
                                    size="sm"
                                    className="h-[36px] font-medium"
                                >
                                    {t('save')}
                                </Button>
                            </Menu.Target>

                            <Menu.Dropdown>
                                <Menu.Item
                                    leftSection={<IconDeviceFloppy size={16} />}
                                    onClick={onUpdatePreset}
                                >
                                    {t('update_current_template')}
                                </Menu.Item>
                                <Menu.Item
                                    leftSection={<IconPlus size={16} />}
                                    onClick={onSaveClick}
                                >
                                    {t('save_as_new_template')}
                                </Menu.Item>
                            </Menu.Dropdown>
                        </Menu>
                    ) : (
                        <Button
                            variant="light"
                            color="indigo"
                            onClick={onSaveClick}
                            leftSection={<IconDeviceFloppy size={16} />}
                            size="sm"
                            className="h-[36px] font-medium"
                        >
                            {t('save')}
                        </Button>
                    )}
                </div>
            </div>

            {/* Template Selection Modal */}
            <CardModal
                opened={opened}
                onClose={() => setOpened(false)}
                header={<Text className="font-semibold text-lg">{t('select_template')}</Text>}
                size="md"
            >
                {savedReports.length === 0 ? (
                    <Text size="sm" className="text-slate-400 text-center py-6">
                        {t('no_data')}
                    </Text>
                ) : (
                    <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
                        {savedReports.map((preset) => {
                            const isActive = preset.id.toString() === selectedPresetId;
                            return (
                                <div
                                    key={preset.id}
                                    className={`flex items-center justify-between p-3 rounded-lg border transition-all ${isActive
                                        ? 'bg-indigo-50/50 border-indigo-200'
                                        : 'bg-slate-50/50 border-slate-100 hover:bg-slate-50 hover:border-slate-200'
                                        }`}
                                >
                                    <div
                                        onClick={() => {
                                            onSelectPreset(preset);
                                            setOpened(false);
                                        }}
                                        className="flex-1 cursor-pointer flex items-center gap-2"
                                    >
                                        <IconTemplate size={16} className={isActive ? 'text-indigo-500' : 'text-slate-400'} />
                                        <span className={`text-sm ${isActive ? 'font-semibold text-indigo-700' : 'text-slate-750'}`}>
                                            {preset.name}
                                        </span>
                                        {isActive && (
                                            <Badge color="indigo" variant="light" size="xs">
                                                {t('active')}
                                            </Badge>
                                        )}
                                    </div>

                                    <ActionIcon
                                        color="red"
                                        variant="subtle"
                                        size="md"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onDeletePreset(preset.id.toString());
                                        }}
                                        title={t('delete')}
                                    >
                                        <IconTrash size={16} />
                                    </ActionIcon>
                                </div>
                            );
                        })}
                    </div>
                )}
            </CardModal>
        </div>
    );
}
